import { useCart } from '@/features/cart/hooks'
import { useShopify } from '@/hooks/useShopify'
import { replaceSchemaVariables } from '@/utils'
import { Link, Progress, Text } from '@lounge-fe/ui-kit'
import { useMoney } from '@lounge-fe/ui-kit/src/hooks/useMoney'
import classNames from 'classnames'
import { useMemo } from 'react'
import { computeEligibleItemsTotal } from '@/features/cart/utils'
import { DiscountProgressProps } from './types'
import { InfoTooltip } from './InfoTooltip'

export const DiscountProgress = ({
  minimumSpend,
  eligibleText,
  eligibleUrl,
  eligibleUrlText,
  eligibleCollections = [],
  ineligibleText,
  infoTooltip,
  showOnComplete = false,
  className,
  ...props
}: DiscountProgressProps) => {
  const { data: { items = [] } = {} } = useCart()
  const { currency, locale } = useShopify()

  const eligibleItemsTotal = useMemo(
    () => computeEligibleItemsTotal(items, eligibleCollections),
    [items, eligibleCollections],
  )
  const minimumSpendMoney = useMoney(
    {
      amount: minimumSpend.toString(),
      currencyCode: currency.active,
    },
    locale,
  )

  const remainingSpendMoney = useMoney(
    {
      amount: (minimumSpend - eligibleItemsTotal).toString(),
      currencyCode: currency.active,
    },
    locale,
  )

  const isEligibleForDiscount = eligibleItemsTotal >= minimumSpend

  return (
    <div
      className={classNames(
        'cart-discount-progress space-y-3 [&_strong]:text-accent p-4',
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-x-2">
        {isEligibleForDiscount ? (
          <>
            <div className="flex items-center gap-x-2 flex-1">
              <Text
                variant="body-sm"
                dangerouslySetInnerHTML={{
                  __html: replaceSchemaVariables(
                    { minimum_spend: minimumSpendMoney.localizedString },
                    eligibleText,
                  ),
                }}
              />
              <InfoTooltip infoTooltip={infoTooltip} />
            </div>
            <Link
              variant="body-sm"
              href={eligibleUrl}
              className="whitespace-nowrap align-self-end capitalize underline"
            >
              {eligibleUrlText}
            </Link>
          </>
        ) : (
          <div className="flex gap-x-2">
            <Text
              variant="body-sm"
              className={!infoTooltip ? 'flex-1' : undefined}
              dangerouslySetInnerHTML={{
                __html: replaceSchemaVariables(
                  {
                    minimum_spend: minimumSpendMoney.localizedString,
                    remaining_spend: remainingSpendMoney.localizedString,
                  },
                  ineligibleText,
                ),
              }}
            />
            <InfoTooltip infoTooltip={infoTooltip} />
          </div>
        )}
      </div>
      {(!isEligibleForDiscount || showOnComplete) && (
        <Progress>
          <Progress.Bar
            className="bg-accent"
            min={0}
            max={minimumSpend}
            value={eligibleItemsTotal}
          />
        </Progress>
      )}
    </div>
  )
}
